// hooks/useWaitingRoom.js
import { useState, useEffect, useRef, useCallback } from 'react';
import instance from '../apis/app';

const POLL_ESPERANDO = 5000;  // en la cola: consultar posicion cada 5 seg
const POLL_ADMITIDO  = 30000; // ya admitido: solo refrescar el contador de sesion
const HB_INTERVAL    = 30000; // heartbeat cada 30 seg

// Si el status falla N veces seguidas, dejamos pasar al usuario. La sala de
// espera es un amortiguador de carga, no un control de acceso: bloquear una
// venta porque no pudimos preguntar por la cola es peor que admitir de mas.
const MAX_FALLOS_ANTES_DE_ABRIR = 2;

export const useWaitingRoom = () => {
    const [state, setState] = useState({
        loading: true,           // cargando estado inicial
        admitted: false,         // usuario puede usar la app
        mustWait: false,         // el servidor dijo explicitamente "waiting"
        position: null,          // posicion en cola
        estimatedWait: null,
        sessionRemainingSeconds: null, // segundos restantes de sesion (null = sin limite)
    });

    const pollRef = useRef(null);
    const heartbeatRef = useRef(null);
    const detenidoRef = useRef(false);
    const fallosRef = useRef(0);
    const admitidoRef = useRef(false);

    const stopPolling = useCallback(() => {
        detenidoRef.current = true;
        clearTimeout(pollRef.current);
        clearInterval(heartbeatRef.current);
    }, []);

    const startHeartbeat = useCallback(() => {
        if (heartbeatRef.current) return;
        heartbeatRef.current = setInterval(async () => {
            try { await instance.post('/waiting-room/heartbeat'); } catch (_) {}
        }, HB_INTERVAL);
    }, []);

    const checkStatus = useCallback(async () => {
        try {
            const res = await instance.get('/waiting-room/status');
            const data = res.data ?? res;
            fallosRef.current = 0;

            if (data.status === 'admitted') {
                admitidoRef.current = true;
                setState({
                    loading: false,
                    admitted: true,
                    mustWait: false,
                    position: null,
                    estimatedWait: null,
                    sessionRemainingSeconds: data.sessionRemainingSeconds ?? null,
                });
                startHeartbeat();
            } else if (data.status === 'session_expired') {
                // Sesion expirada → a la cola de nuevo (WaitingRoomGate redirige)
                admitidoRef.current = false;
                stopPolling();
                setState({ loading: false, admitted: false, mustWait: true, position: null, estimatedWait: null, sessionRemainingSeconds: 0 });
            } else if (data.status === 'waiting') {
                admitidoRef.current = false;
                setState({ loading: false, admitted: false, mustWait: true, position: data.position, estimatedWait: data.estimatedWait, sessionRemainingSeconds: null });
            } else {
                // not_in_queue → unirse
                const joinRes = await instance.post('/waiting-room/join');
                const joinData = joinRes.data ?? joinRes;
                if (joinData.status === 'admitted') {
                    admitidoRef.current = true;
                    setState({ loading: false, admitted: true, mustWait: false, position: null, estimatedWait: null, sessionRemainingSeconds: joinData.sessionRemainingSeconds ?? null });
                    startHeartbeat();
                } else {
                    admitidoRef.current = false;
                    setState({ loading: false, admitted: false, mustWait: true, position: joinData.position, estimatedWait: joinData.estimatedWait, sessionRemainingSeconds: null });
                }
            }
        } catch (error) {
            // ⚠️ Un error de red o un 401 NO significa "tienes que esperar". Antes
            // se caia aca y `admitted` quedaba en false, con lo que el gate mandaba
            // al usuario a la sala de espera (pantalla "Sesion expirada") incluso
            // con la cola desactivada.
            console.error('WaitingRoom error:', error);
            fallosRef.current += 1;
            if (admitidoRef.current || fallosRef.current >= MAX_FALLOS_ANTES_DE_ABRIR) {
                admitidoRef.current = true;
                setState(prev => ({ ...prev, loading: false, admitted: true, mustWait: false }));
            } else {
                setState(prev => ({ ...prev, loading: false, mustWait: false }));
            }
        }
    }, [stopPolling, startHeartbeat]);

    // Poll auto-agendado: 5s mientras espera en la cola, 30s cuando ya esta
    // admitido (solo para refrescar el contador de sesion). Antes eran 5s fijos
    // para siempre, incluso admitido y con la cola apagada.
    const agendarPoll = useCallback(() => {
        if (detenidoRef.current) return;
        const delay = admitidoRef.current ? POLL_ADMITIDO : POLL_ESPERANDO;
        pollRef.current = setTimeout(async () => {
            await checkStatus();
            agendarPoll();
        }, delay);
    }, [checkStatus]);

    useEffect(() => {
        detenidoRef.current = false;
        checkStatus().then(agendarPoll);

        // Al cerrar/salir de la pestana, liberar el slot
        const handleUnload = () => {
            navigator.sendBeacon && navigator.sendBeacon('/waiting-room/leave');
        };
        window.addEventListener('beforeunload', handleUnload);

        return () => {
            stopPolling();
            window.removeEventListener('beforeunload', handleUnload);
        };
    }, []);  // eslint-disable-line

    return state;
};
